import React, { memo } from 'react'
import styled from 'styled-components'
import { GroupProps } from '.'
import { GroupHeader } from '../GroupHeader'
import { URPlusProductResultFromServer } from '../../App'
import { Translate, useTranslations } from '../../TranslationsContext'

export const ImageGrid: React.FC<GroupProps> = memo(
  ({ group, totalGroups, isInlineLoading, onSeeMore, fetchNextPage, children }) => {
    const { outOf, result, results } = useTranslations()

    return (
      <div key={group.title} style={{ paddingBottom: '24px' }}>
        {totalGroups > 1 ? (
          <GroupHeader
            title={group.title}
            pagination={`${group.results.length} ${outOf} ${group.total} ${group.total === 0 ? result : results}`}
            onSeeMore={group.total <= group.results.length ? undefined : () => onSeeMore(group.filter.value)}
          />
        ) : children !== false ? (
          <div>&nbsp;</div>
        ) : (
          <></>
        )}

        <div className="grid-row">
          {group.results
            .map((x) => x as URPlusProductResultFromServer)
            .map((article, i) => {
              return <Result key={i} result={article} />
            })}
        </div>

        {isInlineLoading && (
          <div>
            <h3 className="grid-text-center dots grid-gap3-above grid-gap3-below">
              <Translate id="loading" />
              <span>.</span>
              <span>.</span>
              <span>.</span>
              <p></p>
            </h3>
          </div>
        )}

        {totalGroups === 1 && group.total > group.results.length && (
          <div className="grid-text-center grid-gap2-above">
            <button className="button" onClick={() => fetchNextPage()}>
              <Translate id="loadMoreResults" />
            </button>
          </div>
        )}
      </div>
    )
  }
)

const Img = styled.div<{ src: string }>`
  height: 150px;
  width: 100%;

  background-image: url('${(p) => p.src}');
  background-size: contain;
  background-position: 50% 50%;
  background-repeat: no-repeat;

  margin-bottom: 8px;
`

const PreTitle = styled.p`
  font-family: Roboto;
  font-style: normal;
  font-weight: normal;
  font-size: 12px;
  line-height: 19px;
  text-align: center;
  color: #757575;
  text-transform: uppercase;
  margin-bottom: 4px;
`

const Title = styled.p`
  font-family: Roboto Condensed;
  font-style: normal;
  font-weight: bold;
  font-size: 18px;
  line-height: 20px;

  display: flex;
  align-items: center;
  text-align: center;
  color: #303030;

  min-height: 40px;
  text-align: center;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;

  transition: all 200ms;
`

const Description = styled.p`
  font-family: Roboto;
  font-style: normal;
  font-weight: normal;
  font-size: 14px;
  line-height: 22px;
  text-align: center;
  color: #303030;
  margin-bottom: 0px;
`

const TagBar = styled.div`
  position: absolute;
  top: 19px;
  left: 0px;
  width: 100%;
`

const Tag = styled.span<{ color?: string }>`
  font-family: Roboto Condensed;
  font-style: normal;
  font-weight: bold;
  font-size: 10px;
  display: inline-flex;
  align-items: center;
  text-transform: uppercase;
  padding: 2px 4px;
  border-radius: 4px;

  color: #ffffff;
  background: ${(p) => p.color ?? '#939393'};

  margin: 0 4px 0 0;

  &:first-child {
    margin-left: 22px;
  }

  &[data-content]:after {
    position: absolute;
    top: 28px;
    left: 0;
    width: 100%;
    padding: 12px 16px;
    pointer-events: none;

    font-family: Roboto;
    font-style: normal;
    font-weight: normal;
    font-size: 12px;
    line-height: 19px;

    color: #ffffff;

    content: attr(data-content);
    background: #303030;
    opacity: 0;
    border: 1px solid #212121;
    box-sizing: border-box;
    border-radius: 3px;
    text-transform: none;

    transition: all 200ms;
  }

  &:hover:after {
    opacity: 0.98;
  }
`

const AnchorWrapper = styled.a`
  position: relative;
  padding: 8px 24px 24px 24px;
  text-decoration: none;

  &:hover {
    background-color: white;
    box-shadow: 0px 0px 25px #e7e7e7;

    ${Title} {
      color: #56a0d3;
      text-decoration: underline;
    }
  }

  transition: all 200ms;
`

const Result: React.FC<{ result: URPlusProductResultFromServer }> = memo(({ result }) => {
  const { applicationKitExplanation } = useTranslations()


  return (
    <AnchorWrapper
      className="grid-span4 grid-omega3 grid-gap2-below grid-tablet-span6 grid-tablet-omega2 grid-mobile-span12 grid-mobile-omega1"
      href={result.link}
    >
      <TagBar>
        {result.isApplicationKit && (
          <Tag color="#939393" data-content={applicationKitExplanation}>
            <Translate id="applicationKit" />
          </Tag>
        )}

        {result.isFeatured && (
          <Tag color="#56D39D">
            <Translate id="featured" />
          </Tag>
        )}

        {result.isSolution && (
          <Tag color="#56a0d3">
            <Translate id="solution" />
          </Tag>
        )}
      </TagBar>

      <Img src={result.image} />
      <PreTitle>{result.companyName}</PreTitle>
      <Title>{result.title}</Title>
      <Description>{result.intro}</Description>
    </AnchorWrapper>
  )
})
